import ReactDOM from "react-dom";
import { CartItem } from "components/Cart/Cart";
import {
  convertToWBMP,
  createLabelSVG,
  getDiscardDateTime,
  getImageDataFromSVG,
  processTransitionTime,
} from "features/Mobile/utils";
import { IntlShape } from "react-intl";
import { Brand, logLabelPrint, Country } from "api/index";
import { format } from "date-fns";

interface IPrintLogItem {
  brand?: string;
  store_no?: string;
  champs_no?: string;
  country_iso_code?: string;
  category_id: string;
  category_name: string;
  heading_name: string;
  sub_heading_name: string;
  image: boolean;
  printed_make_local_time: string;
  printed_make_utc: string;
  printed_ready_local_time: string;
  printed_ready_utc: string;
  printed_discard_local_time: string;
  printed_discard_utc: string;
  printed_by_yumid: string;
  printed_at_local_time: string;
  printed_at_utc: string;
  quantity: number;
}

interface onPrintProps {
  cart: CartItem[];
  intl: IntlShape;
  dateTime?: Date | null;
  brand?: Brand;
  country?: Country;
  pic?: string;
  user?: any;
  isReprint?: boolean;
  currentStore?: any;
}

export const onPrint = async ({
  cart,
  intl,
  dateTime,
  brand,
  country,
  pic,
  user,
  isReprint = false,
  currentStore,
}: onPrintProps) => {
  const productsWithSVG = cart.map((product) => ({
    el: createLabelSVG({
      brand,
      pic,
      productHeading: product.heading,
      productSubheading: product.subHeading,
      dateTime: dateTime || Date.now(),
      calculationReady: product.calculationReady,
      calculationDiscard: product.calculationDiscard,
      dateDiscard: product.discardDate,
      locale: intl.locale,
      picText: intl.formatMessage({ id: "label.pic.title" }),
      madeText: intl.formatMessage({ id: "label.made.title" }),
      readyText: intl.formatMessage({ id: "label.ready.title" }),
      discardText: intl.formatMessage({ id: "label.discard.title" }),
    }),
    quantity: product.quantity,
  }));

  const productWithImageData: any[] = await Promise.all(
    productsWithSVG.map((productWithSVG) => {
      const div = document.createElement("div");
      ReactDOM.render(productWithSVG.el, div);

      return new Promise((resolve) => {
        setTimeout(() => {
          const svg = div.innerHTML;
          getImageDataFromSVG(svg, (data: any) => {
            resolve({ imageData: data, quantity: productWithSVG.quantity });
          });
        }, 50);
      });
    })
  );

  const mapp = (window as any).mapp;

  const dateFormatted = (date: Date) => format(date, "yyyy-MM-dd HH:mm:ss");

  const storeNo = currentStore?.id || "";
  const champsNo = currentStore?.champsNumber || "";

  const labelData = {
    labelData: productWithImageData.map(({ quantity, imageData }) => ({
      imageData: {
        width: imageData.width,
        height: imageData.height,
        data: convertToWBMP(imageData.data, imageData.width, imageData.height),
      },
      options: {
        labelHeight: 0.98, // 25 mm
        labelWidth: 1.96, // 50 mm
        gap: 0.05, //1.7 mm
      },
      quantity,
    })),
    logData: cart.reduce((acc: IPrintLogItem[], item) => {
      const now = new Date();
      const readyTime = processTransitionTime(
        dateTime || now,
        item.calculationReady
      );
      const discardTime = getDiscardDateTime({
        calculationDiscard: item.calculationDiscard,
        dateDiscard: item.discardDate,
        readyDateTime: readyTime.date,
        locale: intl.locale,
        brand
      });

      const logItem: IPrintLogItem = {
        brand: brand?.id,
        store_no: storeNo,
        champs_no: champsNo,
        country_iso_code: country?.id,
        category_id: item.category,
        category_name: item.category,
        // heading_id: item.id,
        heading_name: item.heading,
        // sub_heading_id: item.subHeading,
        sub_heading_name: item.subHeading,
        image: Boolean(item.image),
        printed_make_local_time: dateFormatted(now),
        printed_make_utc: now.toISOString(),
        printed_ready_local_time: dateFormatted(readyTime.date),
        printed_ready_utc: readyTime.date.toISOString(),
        printed_discard_local_time: dateFormatted(discardTime.discardDateTime),
        printed_discard_utc: discardTime.discardDateTime.toISOString(),
        printed_by_yumid: user.yumId,
        printed_at_local_time: dateFormatted(now),
        printed_at_utc: now.toISOString(),
        quantity: item.quantity, // this value will be deleted in MOB
      };

      acc.push(logItem);
      return acc;
    }, []),
  };

  // Log the print/reprint event asynchronously (don't block printing)
  // userId and yumId are now extracted from session on backend for security
  // storeNo and champsNo come from currentStore (not user)
  if (brand && country) {
    logLabelPrint({
      brand: brand.id,
      country: country.id,
      storeNo: currentStore?.id || "",
      champsNo: currentStore?.champsNumber || "",
      isReprint,
      products: cart.map((item) => ({
        id: item.id,
        heading: item.heading,
        subHeading: item.subHeading,
        quantity: item.quantity,
        category: item.category,
      })),
    }).catch(() => {
      // Silently handle logging errors to not disrupt printing
    });
  }

  if (!mapp || !mapp?.mrd) {
    return alert("Please run this app inside HutBot");
  }

  mapp.mrd.printLabel(labelData);
};
